/**
 * Read-only view of a Data Agent: what it is, what data it reads, what it knows
 * which queries it ships with and, when Slack is on, its digest format. Shared by the
 * Agent Store and Settings → Data Agents. Everything comes from the /api/datasets
 * payload except the digest format, fetched when its tab opens.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Bot, X, Pencil, Copy, Check, Trash2, MoreVertical, MessageSquare, Search, ChevronRight,
  Database, Table2, Clock, Users, User, Globe, Lock, BookOpen, Slack,
} from 'lucide-react'
import api from '../../services/api'
import { SKILL_CATALOG, inferSkillsFromLegacy } from '../agent-skills/SkillCatalog'
import { columnLabels } from '../digest-layout/SlackLayoutEditor'
import '../../pages/AddAgentWizard.css'
import '../digest-layout/SlackLayoutEditor.css'
import './AgentDetailsDrawer.css'

export interface AgentDetails {
  task_name: string
  display_name: string
  description?: string
  bq_project?: string
  bq_dataset?: string
  bq_tables?: Array<string | { dataset: string; table: string }>
  schedule?: string
  digest_enabled?: boolean
  slack_channel?: string
  slack_channel_id?: string
  allowed_users?: string[]
  context?: string
  questions?: string[]
  premade_queries?: Array<{ name: string; description?: string; sql: string; questions?: string[]; verified?: boolean }>
  column_filters?: Record<string, string[]>
  column_value_filters?: Record<string, Array<{ column: string; operator: string; value: any }>>
  skills?: string[]
  data_lag_days?: number
  managed_by_yaml?: boolean
  google_ads_customer_ids?: string[]
  demand_pulse_datasets?: string[]
  digest_snapshot_sql?: string
  /** Set by /api/datasets: admins, agent managers and admins of a granted group. */
  can_view_details?: boolean
}

export type AgentDetailsTab = 'overview' | 'knowledge' | 'queries' | 'digest'

interface DigestTable {
  id: string
  heading: string
  dim: string
  metrics: string[]
  custom: boolean
  weekly: boolean
}

/** GET /api/agents/{task}/digest-format */
type DigestFormat =
  | { mode: 'agent' }
  | {
      mode: 'engine'
      template: { id: string; name: string } | null
      performance: DigestTable
      tables: DigestTable[]
      metric_labels: Record<string, string>
    }

type DigestState =
  | { state: 'idle' | 'loading' | 'error' }
  | { state: 'ready'; format: DigestFormat }

interface Props {
  agent: AgentDetails
  /** Agent managers get Edit / Duplicate / Delete and see the access list.
   *  Only render the drawer for agents with can_view_details. */
  canManage: boolean
  initialTab?: AgentDetailsTab
  /** Open with the delete confirmation already showing (from a card's ⋯ menu). */
  confirmDeleteOnOpen?: boolean
  onClose: () => void
  onStartChat: () => void
  onEdit: () => void
  onDuplicate: () => void
  /** Resolves to an error message to show, or null on success. */
  onDelete: () => Promise<string | null>
}

interface ContextSection {
  title: string
  body: string
}

const SQL_START = /^(SELECT|FROM|WITH|WHERE|GROUP|ORDER|JOIN|LEFT|RIGHT|INNER|FULL|UNION|CASE|WHEN|END|AND|OR|ON|AS|HAVING|LIMIT)\b/

/** Heading text if the line is a section heading in a task YAML context block. */
function headingOf(line: string): string | null {
  const t = line.trim()
  const boxed = /^[─━═=-]{2,}\s*(.+?)\s*[─━═=-]{2,}$/.exec(t) // ── MANDATORY DATE FILTER ──────
  if (boxed && /[A-Za-z]/.test(boxed[1])) return boxed[1]
  const md = /^#{1,4}\s+(.+)$/.exec(t) // ## Heading
  if (md) return md[1]
  // TABLE SIZES & SAFE WINDOWS — an all-caps line of 2+ words that isn't SQL.
  if (t.length <= 60 && /^[A-Z][A-Z0-9 &/(),'+-]*:?$/.test(t) && t.split(/\s+/).length >= 2 && !SQL_START.test(t)) {
    return t.replace(/:$/, '')
  }
  return null
}

function splitContext(context: string): ContextSection[] {
  const sections: ContextSection[] = []
  let current: ContextSection = { title: 'General', body: '' }
  for (const line of context.split('\n')) {
    const heading = headingOf(line)
    if (heading) {
      if (current.body.trim()) sections.push({ ...current, body: current.body.trim() })
      current = { title: heading, body: '' }
    } else {
      current.body += line + '\n'
    }
  }
  if (current.body.trim()) sections.push({ ...current, body: current.body.trim() })
  return sections
}

function highlight(text: string, query: string) {
  if (!query) return text
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'))
  return parts.map((p, i) => (i % 2 === 1 ? <mark key={i} className="ad-hit">{p}</mark> : p))
}

function tableName(t: string | { dataset: string; table: string }, fallbackDataset: string) {
  if (typeof t !== 'string') return { dataset: t.dataset, table: t.table }
  const parts = t.split('.')
  if (parts.length >= 2) return { dataset: parts[parts.length - 2], table: parts[parts.length - 1] }
  return { dataset: fallbackDataset, table: t }
}

function scheduleLabel(agent: AgentDetails): string | null {
  const cron = (agent.schedule || '').trim()
  if (agent.digest_enabled === false || !cron) return null
  const m = /^0\s+(\d{1,2})\s+\*\s+\*\s+\*$/.exec(cron)
  return m ? `Every day at ${String(+m[1]).padStart(2, '0')}:00 UTC` : `Cron ${cron} (UTC)`
}

export default function AgentDetailsDrawer({
  agent, canManage, initialTab = 'overview', confirmDeleteOnOpen = false,
  onClose, onStartChat, onEdit, onDuplicate, onDelete,
}: Props) {
  const [tab, setTab] = useState<AgentDetailsTab>(initialTab)
  const [menuOpen, setMenuOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(confirmDeleteOnOpen && canManage)
  const [typedName, setTypedName] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  const [query, setQuery] = useState('')
  const [openSections, setOpenSections] = useState<Set<string>>(new Set())
  const [openQueries, setOpenQueries] = useState<Set<number>>(new Set())
  const [copied, setCopied] = useState<number | null>(null)
  const [digest, setDigest] = useState<DigestState>({ state: 'idle' })
  const [sqlOpen, setSqlOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  // Parents pass an inline onClose; a ref keeps the key listener (and focus) stable.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // Each agent opens fresh: a different tab, no leftover search or confirmation.
  useEffect(() => {
    setTab(initialTab)
    setConfirmDelete(confirmDeleteOnOpen && canManage)
    setTypedName('')
    setDeleteError('')
    setQuery('')
    setOpenSections(new Set())
    setOpenQueries(new Set())
    setDigest({ state: 'idle' })
    setSqlOpen(false)
    setMenuOpen(false)
    panelRef.current?.focus()
  }, [agent.task_name, initialTab, confirmDeleteOnOpen, canManage])

  const skills = agent.skills?.length ? agent.skills : inferSkillsFromLegacy(agent)
  const sections = useMemo(() => splitContext(agent.context || ''), [agent.context])
  const q = query.trim().toLowerCase()
  const visibleSections = q
    ? sections.filter((s) => s.title.toLowerCase().includes(q) || s.body.toLowerCase().includes(q))
    : sections
  const tables = (agent.bq_tables || []).map((t) => tableName(t, agent.bq_dataset || ''))
  const questions = (agent.questions || []).filter((x) => x && x.trim())
  const premade = agent.premade_queries || []
  const schedule = scheduleLabel(agent)
  const access = agent.allowed_users || []
  const lag = agent.data_lag_days
  // The Digest tab exists only while the Slack digest is on.
  const shownTab: AgentDetailsTab = tab === 'digest' && !schedule ? 'overview' : tab

  const loadDigest = () => {
    setDigest({ state: 'loading' })
    api
      .get(`/api/agents/${encodeURIComponent(agent.task_name)}/digest-format`)
      .then((res) => setDigest({ state: 'ready', format: res.data }))
      .catch(() => setDigest({ state: 'error' }))
  }

  useEffect(() => {
    if (shownTab === 'digest' && digest.state === 'idle') loadDigest()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownTab, digest.state])

  const toggle = <T,>(set: Set<T>, key: T) => {
    const next = new Set(set)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
  }

  const copySql = async (i: number, sql: string) => {
    try {
      await navigator.clipboard.writeText(sql)
      setCopied(i)
      setTimeout(() => setCopied(null), 1500)
    } catch {
      // Clipboard can be blocked by the browser; the SQL stays selectable.
    }
  }

  const runDelete = async () => {
    setDeleting(true)
    setDeleteError('')
    const err = await onDelete()
    // On success the parent closes the drawer.
    if (err) {
      setDeleteError(err)
      setDeleting(false)
    }
  }

  const tabs: { id: AgentDetailsTab; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'knowledge', label: 'Knowledge', count: sections.length },
    { id: 'queries', label: 'Queries', count: questions.length + premade.length },
    ...(schedule ? [{ id: 'digest' as const, label: 'Slack digest' }] : []),
  ]

  return (
    <div className="ad-overlay" onClick={onClose}>
      <div
        ref={panelRef}
        className="ad-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ad-title"
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation()
          setMenuOpen(false)
        }}
      >
        <div className="ad-header">
          <div className="ad-title-row">
            <div className="ad-avatar"><Bot size={22} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div id="ad-title" className="ad-title">{agent.display_name || agent.task_name}</div>
              <div className="ad-task-name">{agent.task_name}</div>
            </div>
            <button type="button" className="ad-icon-btn" aria-label="Close" onClick={onClose}>
              <X size={18} />
            </button>
          </div>

          {agent.description && <div className="ad-desc">{agent.description}</div>}

          <div className="ad-chips">
            {skills.map((id) => {
              const skill = SKILL_CATALOG[id]
              const Icon = skill?.icon
              return (
                <span key={id} className="ad-chip" title={skill?.description}>
                  {Icon && <Icon size={12} />}
                  {skill?.name || id}
                </span>
              )
            })}
          </div>

          <div className="ad-actions">
            <button type="button" className="st-btn primary" onClick={onStartChat}>
              <MessageSquare size={14} /> Start chat
            </button>
            {canManage && agent.managed_by_yaml !== false && (
              <>
                <button type="button" className="st-btn" onClick={onEdit}>
                  <Pencil size={14} /> Edit
                </button>
                <div className="ad-menu-wrap">
                  <button
                    type="button"
                    className="ad-icon-btn"
                    aria-label="More actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      setMenuOpen((o) => !o)
                    }}
                  >
                    <MoreVertical size={18} />
                  </button>
                  {menuOpen && (
                    <div className="ad-menu" onClick={(e) => e.stopPropagation()}>
                      <button type="button" onClick={() => { setMenuOpen(false); onDuplicate() }}>
                        <Copy size={14} /> Duplicate agent
                      </button>
                      <div className="ad-menu-sep" />
                      <button
                        type="button"
                        className="danger"
                        onClick={() => { setMenuOpen(false); setConfirmDelete(true) }}
                      >
                        <Trash2 size={14} /> Delete agent
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="ad-tabs" role="tablist">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={shownTab === t.id}
                className={`ad-tab${shownTab === t.id ? ' active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
                {t.count !== undefined && <span className="ad-tab-count">{t.count}</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="ad-body">
          {shownTab === 'overview' && (
            <>
              <div className="ad-section">
                <div className="ad-section-title"><Database size={12} /> Data source</div>
                <div className="ad-rows">
                  <div className="ad-row">
                    <div className="ad-row-label">Project</div>
                    <div className="ad-row-value ad-mono">{agent.bq_project || '—'}</div>
                  </div>
                  <div className="ad-row">
                    <div className="ad-row-label">Dataset</div>
                    <div className="ad-row-value ad-mono">{agent.bq_dataset || '—'}</div>
                  </div>
                  {lag !== undefined && lag !== null && (
                    <div className="ad-row">
                      <div className="ad-row-label">Data lag</div>
                      <div className="ad-row-value">
                        {lag} day{lag === 1 ? '' : 's'}
                        <div className="st-hint" style={{ marginTop: 2 }}>
                          Answers use data up to {lag} day{lag === 1 ? '' : 's'} ago.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="ad-section">
                <div className="ad-section-title"><Table2 size={12} /> Tables ({tables.length})</div>
                {tables.length === 0 ? (
                  <div className="st-empty">No tables selected.</div>
                ) : (
                  <div className="ad-rows">
                    {tables.map(({ dataset, table }) => {
                      const full = `${dataset}.${table}`
                      const cols = agent.column_filters?.[table] || agent.column_filters?.[full]
                      const filters = agent.column_value_filters?.[table] || agent.column_value_filters?.[full] || []
                      return (
                        <div key={full} className="ad-row">
                          <div className="ad-row-value">
                            <div className="ad-mono">{dataset !== agent.bq_dataset ? full : table}</div>
                            <div className="ad-table-meta">
                              {cols?.length ? `${cols.length} column${cols.length === 1 ? '' : 's'} visible` : 'All columns visible'}
                            </div>
                            {filters.length > 0 && (
                              <div className="ad-chips" style={{ marginTop: 6 }}>
                                {filters.map((f, i) => (
                                  <span key={i} className="ad-chip ad-mono" title="Applied to every query on this table">
                                    {f.column} {f.operator} {Array.isArray(f.value) ? f.value.join(', ') : String(f.value)}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              <div className="ad-section">
                <div className="ad-section-title"><Clock size={12} /> Slack digest</div>
                <div className="ad-rows">
                  <div className="ad-row">
                    <div className="ad-row-label">Schedule</div>
                    <div className="ad-row-value">{schedule || 'Off'}</div>
                  </div>
                  {schedule && (
                    <div className="ad-row">
                      <div className="ad-row-label">Channel</div>
                      <div className="ad-row-value">
                        {agent.slack_channel ? `#${agent.slack_channel.replace(/^#/, '')}` : agent.slack_channel_id || '—'}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {(agent.google_ads_customer_ids?.length || agent.demand_pulse_datasets?.length) ? (
                <div className="ad-section">
                  <div className="ad-section-title">Connected sources</div>
                  <div className="ad-rows">
                    {!!agent.google_ads_customer_ids?.length && (
                      <div className="ad-row">
                        <div className="ad-row-label">Google Ads</div>
                        <div className="ad-row-value ad-mono">{agent.google_ads_customer_ids.join(', ')}</div>
                      </div>
                    )}
                    {!!agent.demand_pulse_datasets?.length && (
                      <div className="ad-row">
                        <div className="ad-row-label">Demand Pulse</div>
                        <div className="ad-row-value">{agent.demand_pulse_datasets.join(', ')}</div>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {canManage && (
                <div className="ad-section">
                  <div className="ad-section-title"><Users size={12} /> Access</div>
                  {access.length === 0 ? (
                    <div className="ad-chips" style={{ marginTop: 0 }}>
                      <span className="ad-chip"><Lock size={12} /> Admins only</span>
                    </div>
                  ) : access.includes('all') ? (
                    <div className="ad-chips" style={{ marginTop: 0 }}>
                      <span className="ad-chip"><Globe size={12} /> Everyone signed in</span>
                    </div>
                  ) : (
                    <div className="ad-chips" style={{ marginTop: 0 }}>
                      {access.map((a) => (
                        <span key={a} className="ad-chip">
                          {a.startsWith('group:') ? <Users size={12} /> : <User size={12} />}
                          {a.startsWith('group:') ? a.slice(6) : a}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {shownTab === 'knowledge' && (
            <>
              <div className="st-hint" style={{ marginTop: 0, marginBottom: 12 }}>
                Dataset-specific instructions this agent follows. Business rules shared by all
                agents are in Settings → Knowledge.
              </div>
              {sections.length === 0 ? (
                <div className="st-empty">This agent has no dataset context yet.</div>
              ) : (
                <>
                  <div className="ad-search">
                    <Search size={14} />
                    <input
                      type="text"
                      placeholder="Search this agent's knowledge…"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                  </div>
                  {visibleSections.length === 0 && <div className="st-empty">Nothing matches “{query.trim()}”.</div>}
                  {visibleSections.map((s, i) => {
                    const key = `${i}:${s.title}`
                    const open = !!q || openSections.has(key)
                    return (
                      <div key={key} className={`st-premade-card${open ? ' expanded' : ''}`}>
                        <div className="st-premade-header" onClick={() => setOpenSections(toggle(openSections, key))}>
                          <ChevronRight size={14} className={`ad-chevron${open ? ' open' : ''}`} />
                          <span className="st-premade-name">{highlight(s.title, query.trim())}</span>
                          <span className="st-premade-qcount">{s.body.split('\n').length} lines</span>
                        </div>
                        {open && (
                          <div className="st-premade-body">
                            <pre className="ad-pre">{highlight(s.body, query.trim())}</pre>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </>
              )}
            </>
          )}

          {shownTab === 'queries' && (
            <>
              <div className="ad-section">
                <div className="ad-section-title"><BookOpen size={12} /> Example questions ({questions.length})</div>
                {questions.length === 0 ? (
                  <div className="st-empty">No example questions.</div>
                ) : (
                  <div className="ad-rows">
                    {questions.map((x, i) => <div key={i} className="ad-question">{x}</div>)}
                  </div>
                )}
              </div>

              <div className="ad-section">
                <div className="ad-section-title"><Database size={12} /> Premade queries ({premade.length})</div>
                <div className="st-hint" style={{ marginTop: 0, marginBottom: 10 }}>
                  When a question matches one of these, the agent runs the saved SQL instead of writing its own.
                </div>
                {premade.length === 0 ? (
                  <div className="st-empty">No premade queries.</div>
                ) : (
                  premade.map((pq, i) => {
                    const open = openQueries.has(i)
                    return (
                      <div key={i} className={`st-premade-card${open ? ' expanded' : ''}`}>
                        <div className="st-premade-header" onClick={() => setOpenQueries(toggle(openQueries, i))}>
                          <ChevronRight size={14} className={`ad-chevron${open ? ' open' : ''}`} />
                          <span className="st-premade-name">{pq.name}</span>
                          <span className={`st-badge ${pq.verified ? 'verified' : 'draft'}`}>
                            {pq.verified ? 'Verified' : 'Not verified'}
                          </span>
                        </div>
                        {open && (
                          <div className="st-premade-body">
                            {pq.description && <div style={{ fontSize: 13, lineHeight: 1.5 }}>{pq.description}</div>}
                            {!!pq.questions?.length && (
                              <div className="st-premade-row">
                                <label>Answers questions like</label>
                                <div className="ad-chips" style={{ marginTop: 0 }}>
                                  {pq.questions.map((x, j) => <span key={j} className="ad-chip">{x}</span>)}
                                </div>
                              </div>
                            )}
                            <div className="st-premade-row">
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <label style={{ flex: 1 }}>SQL</label>
                                <button type="button" className="st-btn small" onClick={() => copySql(i, pq.sql)}>
                                  {copied === i ? <Check size={12} /> : <Copy size={12} />}
                                  <span style={{ marginLeft: 4 }}>{copied === i ? 'Copied' : 'Copy'}</span>
                                </button>
                              </div>
                              <pre className="ad-pre" style={{ color: 'var(--text)' }}>{pq.sql}</pre>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })
                )}
              </div>
            </>
          )}

          {shownTab === 'digest' && (
            <>
              <div className="ad-section">
                <div className="ad-section-title"><Slack size={12} /> Delivery</div>
                <div className="ad-rows">
                  <div className="ad-row">
                    <div className="ad-row-label">Schedule</div>
                    <div className="ad-row-value">{schedule}</div>
                  </div>
                  <div className="ad-row">
                    <div className="ad-row-label">Channel</div>
                    <div className="ad-row-value">
                      {agent.slack_channel ? `#${agent.slack_channel.replace(/^#/, '')}` : agent.slack_channel_id || '—'}
                    </div>
                  </div>
                  {digest.state === 'ready' && (
                    <div className="ad-row">
                      <div className="ad-row-label">Format</div>
                      <div className="ad-row-value">
                        {digest.format.mode === 'agent'
                          ? 'Written by the agent on each run'
                          : digest.format.template
                            ? `Fixed tables · ${digest.format.template.name} template`
                            : 'Fixed tables · custom snapshot SQL'}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {digest.state === 'loading' && <div className="st-hint">Loading the digest format…</div>}
              {digest.state === 'error' && (
                <div className="st-error">
                  Couldn’t load the digest format.{' '}
                  <button type="button" className="st-btn small" onClick={loadDigest}>Try again</button>
                </div>
              )}

              {digest.state === 'ready' && digest.format.mode === 'agent' && (
                <>
                  <div className="ad-section">
                    <div className="ad-section-title">Message 1 · Overview</div>
                    <div className="sl-mock" style={{ position: 'static' }}>
                      <div className="sl-mock-sec">
                        <div className="sl-mock-heading">Performance</div>
                        <div className="sl-mock-cols">
                          {['Period', 'Revenue', 'Δ%', 'Conversions', 'Δ%', 'EPC', 'Δ%', 'EPV', 'Δ%'].map((c, j) => (
                            <span key={j} className={j === 0 ? 'dim' : ''}>{c}</span>
                          ))}
                        </div>
                      </div>
                      <div className="sl-mock-text">Rows: Last 7 Days, MTD and YoY</div>
                      <div className="sl-mock-text">With a status line, the primary driver and key insights</div>
                    </div>
                  </div>
                  <div className="ad-section">
                    <div className="ad-section-title">Message 2 · Detail tables</div>
                    <div className="st-hint" style={{ marginTop: 0, marginBottom: 8 }}>
                      Tables are chosen on each run to answer these questions from the agent’s YAML, in order:
                    </div>
                    {questions.length === 0 ? (
                      <div className="st-empty">No questions in the YAML.</div>
                    ) : (
                      <div className="ad-rows">
                        {questions.map((x, i) => <div key={i} className="ad-question">{i + 1}. {x}</div>)}
                      </div>
                    )}
                  </div>
                </>
              )}

              {digest.state === 'ready' && digest.format.mode === 'engine' && (() => {
                const f = digest.format
                const cols = (t: DigestTable) =>
                  t.weekly ? ['Week 1', '…', 'Week 6'] : columnLabels(t.metrics, f.metric_labels)
                const card = (t: DigestTable, heading: string) => (
                  <div key={t.id} className="sl-mock-sec">
                    <div className="sl-mock-heading" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {heading}
                      {t.custom && <span className="st-badge draft">Custom columns</span>}
                    </div>
                    <div className="sl-mock-cols">
                      {[t.dim, ...cols(t)].map((c, j) => <span key={j} className={j === 0 ? 'dim' : ''}>{c}</span>)}
                    </div>
                  </div>
                )
                return (
                  <>
                    <div className="ad-section">
                      <div className="ad-section-title">Message 1 · Overview</div>
                      <div className="sl-mock" style={{ position: 'static' }}>
                        {card(f.performance, f.performance.heading)}
                        {!f.performance.custom && f.performance.metrics.includes('conversions') && (
                          <div className="sl-mock-text">Shows Clickouts in place of Conversions when the data has no conversions</div>
                        )}
                        <div className="sl-mock-text">Rows: Last 7 Days, MTD and YTD · Δ% compares each with the period before</div>
                        <div className="sl-mock-text">Followed by the AI summary, key insights and actions</div>
                      </div>
                    </div>
                    <div className="ad-section">
                      <div className="ad-section-title">Message 2 · Detail tables ({f.tables.length})</div>
                      <div className="sl-mock" style={{ position: 'static' }}>
                        {f.tables.map((t, i) =>
                          card(t, `${i + 1}. ${t.weekly ? `6-Week ${f.metric_labels[t.metrics[0]] || t.metrics[0]} Trend` : t.heading}`)
                        )}
                        <div className="sl-mock-text">Δ% compares the last 7 days with the 7 days before</div>
                      </div>
                    </div>
                    {!!agent.digest_snapshot_sql?.trim() && (
                      <div className="ad-section">
                        <div className={`st-premade-card${sqlOpen ? ' expanded' : ''}`}>
                          <div className="st-premade-header" onClick={() => setSqlOpen(!sqlOpen)}>
                            <ChevronRight size={14} className={`ad-chevron${sqlOpen ? ' open' : ''}`} />
                            <span className="st-premade-name">Snapshot SQL</span>
                            <span className="st-premade-qcount">{agent.digest_snapshot_sql.split('\n').length} lines</span>
                          </div>
                          {sqlOpen && (
                            <div className="st-premade-body">
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span className="st-hint" style={{ flex: 1, marginTop: 0 }}>
                                  Runs once per digest; every table above is read from its result.
                                </span>
                                <button type="button" className="st-btn small" onClick={() => copySql(-1, agent.digest_snapshot_sql || '')}>
                                  {copied === -1 ? <Check size={12} /> : <Copy size={12} />}
                                  <span style={{ marginLeft: 4 }}>{copied === -1 ? 'Copied' : 'Copy'}</span>
                                </button>
                              </div>
                              <pre className="ad-pre" style={{ color: 'var(--text)' }}>{agent.digest_snapshot_sql}</pre>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )
              })()}
            </>
          )}
        </div>

        {confirmDelete && (
          <div className="ad-confirm">
            <div className="ad-confirm-title">Delete this agent?</div>
            <div className="st-hint" style={{ marginTop: 0 }}>
              This removes the agent’s configuration and can’t be undone. Type{' '}
              <span className="ad-mono" style={{ color: 'var(--text)' }}>{agent.task_name}</span> to confirm.
            </div>
            <input
              type="text"
              autoFocus
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && typedName === agent.task_name && !deleting) runDelete()
              }}
              aria-label="Agent name to confirm deletion"
            />
            {deleteError && <div className="st-error">{deleteError}</div>}
            <div className="ad-confirm-actions">
              <button
                type="button"
                className="st-btn"
                disabled={deleting}
                onClick={() => { setConfirmDelete(false); setTypedName(''); setDeleteError('') }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="st-btn danger"
                disabled={typedName !== agent.task_name || deleting}
                onClick={runDelete}
              >
                {deleting ? 'Deleting…' : 'Delete agent'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
